import type { Editor, JSONContent } from "@tiptap/core";
import type { Block } from "@/lib/api";

/**
 * Apply the rest of a rebase to the canvas: remove the blocks the server
 * deleted and swap in the server's versions of blocks it converted or edited
 * (mergeOnRebase decides which; this only applies it).
 *
 * One transaction, last position first, so earlier positions stay valid and
 * the caret is mapped through the change rather than reset — the author may be
 * typing in another block while this lands.
 */
export function applyRebase(
  editor: Editor,
  removals: readonly string[],
  replacements: readonly Block[],
  toNode: (block: Block) => JSONContent | null,
): number {
  const remove = new Set(removals);
  const replace = new Map(replacements.map((b) => [b.id, b]));
  const targets: { pos: number; size: number; id: string }[] = [];
  editor.state.doc.forEach((node, offset) => {
    const id = node.attrs?.blockId as string | undefined;
    if (id && (remove.has(id) || replace.has(id))) targets.push({ pos: offset, size: node.nodeSize, id });
  });
  if (targets.length === 0) return 0;

  const tr = editor.state.tr;
  for (const t of [...targets].reverse()) {
    const incoming = replace.get(t.id);
    const json = incoming ? toNode(incoming) : null;
    if (json) tr.replaceWith(t.pos, t.pos + t.size, editor.schema.nodeFromJSON(json));
    else if (remove.has(t.id)) tr.delete(t.pos, t.pos + t.size);
  }
  editor.view.dispatch(tr);
  return targets.length;
}
